/**
 * Cart Pack Quantities
 * Enhances cart quantity controls with metaobject-based pack increments
 */

class CartPackQuantities {
  constructor() {
    this.initialized = false;
    this.metaobjectData = null;
  }

  init() {
    if (this.initialized) return;
    
    this.loadMetaobjectData();
    this.enhanceQuantityControls();
    this.initialized = true;
    
    console.log('🛒 Cart Pack Quantities initialized');
  }

  loadMetaobjectData() {
    const metaDataElement = document.getElementById('cart-metaobject-packs-data');
    
    if (metaDataElement) {
      try {
        this.metaobjectData = JSON.parse(metaDataElement.textContent);
        console.log('📊 Loaded cart metaobject data:', this.metaobjectData);
      } catch (error) {
        console.error('Error parsing cart metaobject data:', error);
      }
    } else {
      console.log('⚠️ No cart metaobject data found, using fallback quantities');
    }
  }

  getPackQuantityForType(productType) {
    if (!productType) return 1;
    
    // Convert product type to handle format
    const productTypeHandle = productType.toLowerCase().replace(/\s+/g, '-');
    
    // Try metaobject data first
    if (this.metaobjectData && this.metaobjectData[productTypeHandle]) {
      const packData = this.metaobjectData[productTypeHandle];
      return packData.packs_of || 1;
    }
    
    // Fallback to hardcoded quantities
    const defaultQuantities = {
      'art print': 2,
      'art-print': 2,
      'bookmark': 7,
      'greeting card': 8,
      'greeting-card': 8,
      'wrapping paper': 25,
      'wrapping-paper': 25,
      'postcard': 1,
      'sticker': 1,
      'sticker set': 1,
      'sticker-set': 1,
      'pin badge': 1,
      'pin-badge': 1
    };
    
    return defaultQuantities[productTypeHandle] || 1;
  }

  enhanceQuantityControls() {
    // Find all quantity inputs in the cart
    const quantityInputs = document.querySelectorAll('.cart-quantity .quantity__input');
    
    console.log('🔍 Found', quantityInputs.length, 'cart quantity inputs to enhance');
    
    quantityInputs.forEach((input, index) => {
      this.enhanceQuantityInput(input, index);
    });
  }

  enhanceQuantityInput(input, index) {
    try {
      const productType = input.dataset.productType;
      const packIncrement = parseInt(input.dataset.packIncrement) || this.getPackQuantityForType(productType);
      
      // Get the quantity control container
      const quantityControl = input.closest('quantity-input');
      if (!quantityControl) return;
      
      // Get the plus and minus buttons
      const minusButton = quantityControl.querySelector('button[name="minus"]');
      const plusButton = quantityControl.querySelector('button[name="plus"]');
      
      if (!minusButton || !plusButton) return;
      
      // Override the default click behavior
      this.overrideButtonBehavior(minusButton, input, -packIncrement, productType);
      this.overrideButtonBehavior(plusButton, input, packIncrement, productType);
      
      // Validate current quantity is a multiple of pack increment
      this.validateQuantityMultiple(input, packIncrement);
      
      console.log(`✅ Enhanced cart quantity input ${index}:`, {
        productType: productType,
        packIncrement: packIncrement,
        currentQuantity: input.value
      });
      
    } catch (error) {
      console.error(`Error enhancing cart quantity input ${index}:`, error);
    }
  }

  overrideButtonBehavior(button, input, increment, productType) {
    // Remove existing event listeners by cloning the button
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    // Add our custom event listener
    newButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      this.handleQuantityChange(input, increment, productType);
    });
  }

  handleQuantityChange(input, increment, productType) {
    const currentQuantity = parseInt(input.value) || 0;
    const packIncrement = Math.abs(increment);
    let newQuantity = currentQuantity + increment;
    
    // Ensure minimum quantity is at least one pack
    if (newQuantity < packIncrement) {
      newQuantity = increment > 0 ? packIncrement : 0;
    }
    
    // Ensure quantity is a multiple of pack increment (unless going to 0)
    if (newQuantity > 0) {
      const remainder = newQuantity % packIncrement;
      if (remainder !== 0) {
        if (increment > 0) {
          // Round up to next multiple
          newQuantity = newQuantity + (packIncrement - remainder);
        } else {
          // Round down to previous multiple
          newQuantity = newQuantity - remainder;
        }
      }
    }
    
    console.log('🔄 Cart quantity change:', {
      productType: productType,
      currentQuantity: currentQuantity,
      increment: increment,
      newQuantity: newQuantity,
      packIncrement: packIncrement
    });
    
    // Update the input value
    input.value = newQuantity;
    
    // Trigger change event to update cart
    input.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Show feedback message
    this.showQuantityChangeMessage(productType, increment, packIncrement);
  }

  validateQuantityMultiple(input, packIncrement) {
    const currentQuantity = parseInt(input.value) || 0;
    
    if (currentQuantity > 0 && currentQuantity % packIncrement !== 0) {
      // Adjust to nearest valid multiple
      const nearestMultiple = Math.round(currentQuantity / packIncrement) * packIncrement;
      const adjustedQuantity = nearestMultiple > 0 ? nearestMultiple : packIncrement;
      
      console.log('🔧 Adjusting invalid quantity:', {
        original: currentQuantity,
        adjusted: adjustedQuantity,
        packIncrement: packIncrement
      });
      
      input.value = adjustedQuantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  showQuantityChangeMessage(productType, increment, packIncrement) {
    // Remove existing message
    const existingMessage = document.querySelector('.cart-quantity-change-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create new message
    const message = document.createElement('div');
    message.className = 'cart-quantity-change-message';
    
    const action = increment > 0 ? 'Added' : 'Removed';
    const amount = Math.abs(increment);
    const unit = amount === 1 ? 'item' : 'items';
    
    message.innerHTML = `
      <div class="cart-quantity-message-content">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path d="M8.33333 13.3333L5 10L3.82167 11.1783L8.33333 15.6667L17.5 6.5L16.3217 5.32167L8.33333 13.3333Z" fill="currentColor"/>
        </svg>
        <span>${action} ${amount} ${unit} (${productType})</span>
      </div>
    `;
    
    // Style the message
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 10000;
      animation: slideInRight 0.3s ease;
      box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
    `;
    
    document.body.appendChild(message);
    
    // Remove after 2 seconds
    setTimeout(() => {
      message.style.animation = 'slideOutRight 0.3s ease forwards';
      setTimeout(() => {
        if (message.parentNode) {
          document.body.removeChild(message);
        }
      }, 300);
    }, 2000);
  }

  // Method to handle cart updates from external sources
  handleCartUpdate() {
    // Re-enhance quantity controls after cart updates
    setTimeout(() => {
      this.enhanceQuantityControls();
    }, 100);
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  .cart-quantity-message-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .cart-quantity-message-content svg {
    flex-shrink: 0;
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const cartPackQuantities = new CartPackQuantities();
  cartPackQuantities.init();
  
  // Re-enhance after cart updates (DOM event)
  document.addEventListener('cart:updated', () => {
    cartPackQuantities.handleCartUpdate();
  });

  // Re-enhance after Dawn's pub/sub cart update
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      cartPackQuantities.handleCartUpdate();
    });
  }

  // Also listen for section reloads (Shopify's cart updates)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.id && event.target.id.includes('cart')) {
      setTimeout(() => {
        cartPackQuantities.handleCartUpdate();
      }, 200);
    }
  });
});

// Export for external use
window.CartPackQuantities = CartPackQuantities;